import { iOrder } from "core/kangarooTypes/iOrder";
import { updateOrder } from "core/Services/CommonServices";
import { getOrders } from "core/Services/MerchantServices";
import { X } from "lucide-react";
import { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { toast } from "react-toastify";
import Button from "shared/Button";
import { Dialog } from "shared/Dialog/Dialog";
import { DialogContent } from "shared/Dialog/DialogContent";
import { DialogFooter } from "shared/Dialog/DialogFooter";
import { DialogHeader } from "shared/Dialog/DialogHeader";
import { DialogTitle } from "shared/Dialog/DialogTitle";
import { formatDate } from "utils/DatesUtil";
import { useTranslation } from "react-i18next";

const SortDialog = ({
  isOpen,
  onClose,
  driverId,
}: {
  isOpen: boolean;
  onClose: () => void;
  driverId: string;
}) => {
  const { t } = useTranslation();
  const [orders, setOrders] = useState<iOrder[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [movedOrderIds, setMovedOrderIds] = useState<string[]>([]);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setIsLoading(true);
        const response = await getOrders({
          driver_id: driverId,
          multiple_status: ["assigned", "in_progress"],
        });
        if (response && response.data) {
          const sortedOrders = response.data.sort(
            (a, b) => a.sort_index - b.sort_index
          );
          setOrders(sortedOrders);
        }
      } catch (error) {
        console.error("Failed to fetch orders:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (isOpen) {
      fetchOrders();
    }
  }, [isOpen, driverId]);

  const handleSortIndexChange = (orderId: string, value: number) => {
    const newOrders = [...orders];
    const orderIndex = newOrders.findIndex(
      (order) => order.id === Number(orderId)
    );

    if (orderIndex !== -1) {
      // Update the sort index
      newOrders[orderIndex].sort_index = value;

      // Sort the orders by sort_index
      const sortedOrders = [...newOrders].sort(
        (a, b) => a.sort_index - b.sort_index
      );

      // Track which orders have moved for animation
      const movedIds: string[] = [];
      for (let i = 0; i < sortedOrders.length; i++) {
        if (sortedOrders[i].id !== newOrders[i].id) {
          movedIds.push(sortedOrders[i].id.toString());
        }
      }

      setMovedOrderIds(movedIds);

      // Clear the moved IDs after animation completes
      setTimeout(() => {
        setMovedOrderIds([]);
      }, 1000);

      setOrders(sortedOrders);
    }
  };

  const handleUpdateOrder = async () => {
    try {
      setIsSubmitting(true);
      await Promise.all(
        orders.map((order) =>
          updateOrder({ sort_index: order.sort_index }, order.id)
        )
      );
      toast.success(t("sortDialog.changesApplied"));
    } catch (error: any) {
      toast.error(error.response.data.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex justify-between items-center">
            <span>{t("sortDialog.title")}</span>
            <button
              onClick={onClose}
              className="rounded-full p-1 hover:bg-gray-100 transition-colors"
            >
              <X size={18} />
            </button>
          </DialogTitle>
        </DialogHeader>
        <div className="p-4 flex items-start justify-start flex-col h-full">
          {isLoading ? (
            <table className="w-full border-collapse border border-gray-200">
              <thead>
                <tr className="bg-gray-50">
                  {[
                    t("sortDialog.orderId"),
                    t("sortDialog.merchantName"),
                    t("sortDialog.customerName"),
                    t("sortDialog.createdAt"),
                    t("sortDialog.sortIndex"),
                  ].map((header, index) => (
                    <th
                      key={index}
                      className="p-2 border border-gray-200 text-left"
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...Array(5)].map((_, index) => (
                  <tr key={index}>
                    {[...Array(5)].map((_, colIndex) => (
                      <td key={colIndex} className="p-2 border border-gray-200">
                        <Skeleton height={20} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="w-full">
              <div className="hidden sm:block">
                <table className="w-full border-collapse border border-gray-200">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="p-2 border border-gray-200 text-left">
                        {t("sortDialog.orderId")}
                      </th>
                      <th className="p-2 border border-gray-200 text-left">
                        {t("sortDialog.merchantName")}
                      </th>
                      <th className="p-2 border border-gray-200 text-left">
                        {t("sortDialog.customerName")}
                      </th>
                      <th className="p-2 border border-gray-200 text-left">
                        {t("sortDialog.createdAt")}
                      </th>
                      <th className="p-2 border border-gray-200 text-left">
                        {t("sortDialog.sortIndex")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order) => (
                      <tr
                        key={order.id}
                        className={`hover:bg-gray-50 transition-all duration-500 ${
                          movedOrderIds.includes(order.id.toString())
                            ? "bg-yellow-100 shadow-md"
                            : ""
                        }`}
                      >
                        <td className="p-2 border border-gray-200">
                          {order.id}
                        </td>
                        <td className="p-2 border border-gray-200">
                          {order.merchant_name}
                        </td>
                        <td className="p-2 border border-gray-200">
                          {order.customer_name}
                        </td>
                        <td className="p-2 border border-gray-200">
                          {formatDate(order.created_at)}
                        </td>
                        <td className="p-2 border border-gray-200">
                          <input
                            type="number"
                            value={order.sort_index}
                            onChange={(e) =>
                              handleSortIndexChange(
                                order.id.toString(),
                                parseInt(e.target.value) || 0
                              )
                            }
                            className="p-1 border border-gray-300 rounded w-20"
                            min="0"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="block sm:hidden">
                {orders.map((order) => (
                  <div
                    key={order.id}
                    className={`border border-gray-200 rounded-lg p-4 mb-4 shadow-sm transition-all duration-500 ${
                      movedOrderIds.includes(order.id.toString())
                        ? "bg-yellow-100 shadow-md translate-y-0"
                        : ""
                    }`}
                  >
                    <div className="mb-2">
                      <strong>{t("sortDialog.orderId")}:</strong> {order.id}
                    </div>
                    <div className="mb-2">
                      <strong>{t("sortDialog.merchantName")}:</strong>{" "}
                      {order.merchant_name}
                    </div>
                    <div className="mb-2">
                      <strong>{t("sortDialog.customerName")}:</strong>{" "}
                      {order.customer_name}
                    </div>
                    <div className="mb-2">
                      <strong>{t("sortDialog.createdAt")}:</strong>{" "}
                      {formatDate(order.created_at)}
                    </div>
                    <div className="mb-2">
                      <strong>{t("sortDialog.sortIndex")}:</strong>{" "}
                      <input
                        type="number"
                        value={order.sort_index}
                        onChange={(e) =>
                          handleSortIndexChange(
                            order.id.toString(),
                            parseInt(e.target.value) || 0
                          )
                        }
                        className="p-1 border border-gray-300 rounded w-20 mt-1"
                        min="0"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter className="mt-4 sm:mt-6 flex flex-col sm:flex-row gap-2 bg-white">
          <Button
            type="button"
            variant="outlined"
            onClick={onClose}
            disabled={isSubmitting}
          >
            {t("sortDialog.cancel")}
          </Button>
          <Button onClick={handleUpdateOrder} isLoading={isSubmitting}>
            {t("sortDialog.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SortDialog;
